import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAllDeviceFamilies } from 'api/device_family';
import { addNewDevice } from 'api/devices';
import { Button } from './ui/Button';
import { colors } from '../theme/colors';

interface DeviceFamily {
  id: number;
  device_name: string;
  is_active: boolean;
  adding_time: string;
  data_schema: {
    point_name: string;
    measured_values: string;
    description: string;
  }[];
  description: string;
}

interface FormValues {
  deviceFamilyId: number;
  description: string;
}


interface AddDeviceFormProps {
  onSuccess: () => void;
}

const AddDeviceForm: React.FC<AddDeviceFormProps> = ({ onSuccess }) => {
  const [deviceFamilyId, setDeviceFamilyId] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [deviceFamilies, setDeviceFamilies] = useState<DeviceFamily[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeviceFamilies = async () => {
      try {
        const families: DeviceFamily[] = await getAllDeviceFamilies();
        setDeviceFamilies(families);
      } catch (error) {
        console.error('Error fetching device families:', error);
      }
    };

    fetchDeviceFamilies();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (deviceFamilyId) {
      try {
        setIsLoading(true);
        setError(null);
        await addNewDevice(deviceFamilyId, description);
        setIsSuccess(true);

        // Reset form after short delay
        setTimeout(() => {
          setDeviceFamilyId(null);
          setDescription('');
          setIsSuccess(false);
          onSuccess();
        }, 1500);
      } catch (err) {
        setError('Не удалось добавить устройство. Попробуйте снова.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        Добавить новое устройство
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isSuccess && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            mb: 3,
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          Устройство успешно добавлено!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Семейство устройства</InputLabel>
          <Select
            value={deviceFamilyId || ''}
            label="Семейство устройства"
            onChange={(event) => setDeviceFamilyId(event.target.value as number)}
            required
            disabled={isLoading || isSuccess}
          >
            {deviceFamilies.map((deviceFamily) => (
              <MenuItem key={deviceFamily.id} value={deviceFamily.id}>
                {deviceFamily.device_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Описание"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          required
          disabled={isLoading || isSuccess}
          sx={{ mb: 3 }}
          placeholder="Введите описание устройства..."
        />

        <Button
          type="submit"
          variant="gradient"
          fullWidth
          disabled={isLoading || isSuccess}
          loading={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
              Добавление...
            </>
          ) : (
            'Добавить устройство'
          )}
        </Button>
      </form>
    </Box>
  );
};

export default AddDeviceForm;